/* eslint-disable camelcase -- Firestore field names are the persisted Turkish schema. */
// Akış gönderisi (kullanıcı paylaşımı) kuralları: API, akış normalizasyonu ve arayüz aynı sınırları kullanır.
export const FEED_POST_MAX_LENGTH = 500;
export const FEED_POST_DAILY_LIMIT = 3;
// İki ayrı kişinin şikâyeti gönderiyi admin bakana kadar gizler.
export const FEED_POST_HIDE_AT_REPORTS = 2;
const MAX_URL_LENGTH = 500;

// Fotoğraf genişlik / yükseklik oranı (maç ve gönderi fotoğrafı); uç değerler saklanmaz.
export function photoRatio(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const ratio = Number(value);
  if (!Number.isFinite(ratio) || ratio < 0.2 || ratio > 5) return undefined;
  return Math.round(ratio * 1000) / 1000;
}

// Kısaltılmış bağlantı hedefi gizler; kabul edilmez.
const SHORTENERS = [
  'bit.ly', 't.co', 'tinyurl.com', 'goo.gl', 'ow.ly', 'is.gd', 'cutt.ly', 'shorturl.at',
  'rebrand.ly', 'buff.ly', 't.ly', 'rb.gy', 'tiny.cc', 'shorte.st', 'adf.ly', 'bl.ink', 'lnkd.in',
];

export type FeedLinkError = 'LINK_INVALID' | 'LINK_SHORTENER';

const hostIs = (host: string, domain: string) => host === domain || host.endsWith(`.${domain}`);

/** Bağlantıyı doğrular ve türünü çıkarır. Video sitelerinde oynatıcı kimliği döner. */
export function parseFeedLink(value: unknown): LeagueFeed.Link | FeedLinkError {
  if (typeof value !== 'string') return 'LINK_INVALID';
  const raw = value.trim();
  if (!raw || raw.length > MAX_URL_LENGTH) return 'LINK_INVALID';
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return 'LINK_INVALID';
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  if (
    !['http:', 'https:'].includes(url.protocol) ||
    url.username ||
    url.password ||
    url.port ||
    !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(host) ||
    /^\d+(\.\d+){3}$/.test(host)
  ) {
    return 'LINK_INVALID';
  }
  if (SHORTENERS.some(domain => hostIs(host, domain))) return 'LINK_SHORTENER';
  url.protocol = 'https:';
  url.hash = '';
  const base = { url: url.toString(), alan: host };

  if (hostIs(host, 'youtube.com') || host === 'youtu.be') {
    const path = url.pathname.split('/').filter(Boolean);
    const id =
      host === 'youtu.be'
        ? path[0]
        : path[0] === 'watch'
        ? url.searchParams.get('v')
        : ['shorts', 'embed', 'live'].includes(path[0])
        ? path[1]
        : null;
    if (id && /^[A-Za-z0-9_-]{11}$/.test(id)) {
      return { ...base, tur: 'video', saglayici: 'youtube', video_id: id };
    }
    return { ...base, tur: 'link' };
  }
  if (hostIs(host, 'vimeo.com')) {
    const id = url.pathname.split('/').filter(Boolean).find(part => /^\d{5,12}$/.test(part));
    return id ? { ...base, tur: 'video', saglayici: 'vimeo', video_id: id } : { ...base, tur: 'link' };
  }
  if (hostIs(host, 'instagram.com') && /^\/(p|reel|reels|tv)\//.test(url.pathname)) {
    return { ...base, tur: 'video', saglayici: 'instagram' };
  }
  if (hostIs(host, 'tiktok.com') && /\/video\/\d+/.test(url.pathname)) {
    return { ...base, tur: 'video', saglayici: 'tiktok' };
  }
  return { ...base, tur: 'link' };
}

/** Kayıttan okunan bağlantıyı yeniden doğrular (kayıt bozulmuşsa kart bağlantısız çizilir). */
export function toFeedLink(value: any): LeagueFeed.Link | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const parsed = parseFeedLink(value.url);
  if (typeof parsed === 'string') return undefined;
  const title = typeof value.baslik === 'string' ? value.baslik.trim().slice(0, 160) : '';
  return title ? { ...parsed, baslik: title } : parsed;
}

/** Video sitesinin gömülü oynatıcısı; yalnız kullanıcı tıklayınca yüklenir. */
export function feedVideoEmbed(link: LeagueFeed.Link): string | null {
  if (link.saglayici === 'youtube' && link.video_id) {
    return `https://www.youtube-nocookie.com/embed/${link.video_id}?autoplay=1&rel=0`;
  }
  if (link.saglayici === 'vimeo' && link.video_id) {
    return `https://player.vimeo.com/video/${link.video_id}?autoplay=1&dnt=1`;
  }
  return null;
}

export const feedVideoThumbnail = (link: LeagueFeed.Link) =>
  link.saglayici === 'youtube' && link.video_id
    ? `https://i.ytimg.com/vi/${link.video_id}/hqdefault.jpg`
    : null;
